import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { api, formatCurrency, MONTHS } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Dashboard() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hiddenAmounts, setHiddenAmounts] = useState(() => {
    try {
      const saved = localStorage.getItem('hiddenAmounts');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [expandedCats, setExpandedCats] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sum, txns] = await Promise.all([
        api.get(`/transactions/summary?month=${month}&year=${year}`),
        api.get(`/transactions?month=${month}&year=${year}&limit=5`),
      ]);
      setSummary(sum);
      setRecent(txns);
    } catch {}
    setLoading(false);
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const currency = user?.currency || 'COP';

  const toggleHide = (key) => setHiddenAmounts(p => {
    const next = { ...p, [key]: !p[key] };
    localStorage.setItem('hiddenAmounts', JSON.stringify(next));
    return next;
  });
  const toggleCat = (id) => setExpandedCats(p => ({ ...p, [id]: !p[id] }));

  const MaskedAmount = ({ value, id, color, size = '2.4rem' }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: size, fontFamily: 'var(--font-display)', fontWeight: 800, color }}>
        {hiddenAmounts[id] ? '••••••' : formatCurrency(value, currency)}
      </span>
      <button onClick={() => toggleHide(id)} style={styles.eyeBtn} title={hiddenAmounts[id] ? 'Mostrar' : 'Ocultar'}>
        {hiddenAmounts[id] ? '🙈' : '👁️'}
      </button>
    </div>
  );

  return (
    <div style={styles.page} className="animate-fadeIn">
      {/* Period selector */}
      <div style={styles.periodRow}>
        <select className="form-select" value={month} onChange={e => setMonth(Number(e.target.value))} style={styles.periodSelect}>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <select className="form-select" value={year} onChange={e => setYear(Number(e.target.value))} style={styles.periodSelect}>
          {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" style={{ width: 36, height: 36 }} />
        </div>
      ) : (
        <>
          {/* Balance hero */}
          <div style={styles.balanceCard}>
            <div style={styles.balanceGlow} />
            <p style={styles.balanceLabel}>Balance disponible</p>
              <MaskedAmount
                value={summary?.total_balance || 0}
                id="balance"
                color={summary?.total_balance >= 0 ? 'var(--green)' : 'var(--red)'}
              />

              {summary?.previous_balance !== 0 && (
                <div style={styles.prevBalance}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    Saldo anterior
                  </span>
                  <span style={{
                    fontSize: '0.88rem', fontWeight: 600,
                    color: summary?.previous_balance >= 0 ? 'var(--green)' : 'var(--red)'
                  }}>
                    {hiddenAmounts['balance'] ? '••••••' : formatCurrency(summary?.previous_balance || 0, currency)}
                  </span>
                </div>
              )}
            <div style={styles.balanceRow}>
              <div style={styles.balanceStat}>
                <span style={styles.balanceStatIcon}>↑</span>
                <div>
                  <p style={styles.balanceStatLabel}>Ingresos</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <p style={{ ...styles.balanceStatVal, color: 'var(--green)' }}>
                      {hiddenAmounts['income'] ? '••••••' : formatCurrency(summary?.total_income || 0, currency)}
                    </p>
                    <button onClick={() => toggleHide('income')} style={styles.eyeBtnSm}>
                      {hiddenAmounts['income'] ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>
              <div style={styles.divider} />
              <div style={styles.balanceStat}>
                <span style={{ ...styles.balanceStatIcon, color: 'var(--red)' }}>↓</span>
                <div>
                  <p style={styles.balanceStatLabel}>Gastos</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <p style={{ ...styles.balanceStatVal, color: 'var(--red)' }}>
                      {hiddenAmounts['expense'] ? '••••••' : formatCurrency(summary?.total_expense || 0, currency)}
                    </p>
                    <button onClick={() => toggleHide('expense')} style={styles.eyeBtnSm}>
                      {hiddenAmounts['expense'] ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Chart + Categories */}
          {summary?.by_category?.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Gastos por categoría</h3>
              <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Donut chart */}
                <div style={{ width: 160, height: 160, flexShrink: 0, margin: '0 auto', position: 'relative' }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={summary.by_category} dataKey="total" cx="50%" cy="50%"
                        innerRadius={45} outerRadius={72} paddingAngle={3}>
                        {summary.by_category.map((c, i) => (
                          <Cell key={i} fill={c.color || '#7C6FFF'} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, name) => [formatCurrency(v, currency), name]}
                        contentStyle={{ background: '#16161F', border: '1px solid #2A2A3A', borderRadius: 10, color: '#F0F0F8' }}
                        labelStyle={{ color: '#F0F0F8' }}
                        itemStyle={{ color: '#F0F0F8' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Category list with accordion */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 180 }}>
                  {summary.by_category.map((c, i) => {
                    const pct = summary.total_expense > 0 ? Math.round((c.total / summary.total_expense) * 100) : 0;
                    const hasSubs = c.subcategories && c.subcategories.length > 0;
                    const isOpen = expandedCats[c.cat_id];

                    return (
                      <div key={i}>
                        {/* Parent row */}
                        <div
                          style={{ ...styles.catRow, cursor: hasSubs ? 'pointer' : 'default' }}
                          onClick={() => hasSubs && toggleCat(c.cat_id)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 14 }}>{c.icon}</span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 500 }} className="truncate">{c.name}</span>
                            {hasSubs && (
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', display:'inline-block' }}>▼</span>
                            )}
                          </div>
                          <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', flexShrink: 0 }}>{pct}%</span>
                        </div>
                        <div className="progress-bar" style={{ marginBottom: 4 }}>
                          <div className="progress-fill" style={{ width: `${pct}%`, background: c.color }} />
                        </div>

                        {/* Subcategories accordion */}
                        {hasSubs && isOpen && (
                          <div style={styles.subList}>
                            {c.subcategories.map((s, j) => {
                              const sPct = c.total > 0 ? Math.round((s.total / c.total) * 100) : 0;
                              return (
                                <div key={j} style={styles.subItem}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display:'flex', gap:4 }}>
                                      <span>{s.icon}</span> {s.name}
                                    </span>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                      {formatCurrency(s.total, currency)} · {sPct}%
                                    </span>
                                  </div>
                                  <div className="progress-bar" style={{ height: 4 }}>
                                    <div className="progress-fill" style={{ width: `${sPct}%`, background: s.color || c.color }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Recent transactions */}
          <div style={styles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={styles.sectionTitle}>Últimos movimientos</h3>
              <Link to="/transactions" className="btn btn-ghost btn-sm">Ver todos →</Link>
            </div>
            {recent.length === 0 ? (
              <div style={styles.empty}>
                <span style={{ fontSize: 40 }}>📊</span>
                <p>Sin movimientos este mes</p>
                <Link to="/transactions" className="btn btn-primary btn-sm">+ Agregar</Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {recent.map((t) => (
                  <div key={t.id} className="card card-sm" style={styles.txnItem}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                      background: t.category_color ? `${t.category_color}20` : 'var(--bg-input)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                    }}>
                      {t.category_icon || (t.type === 'income' ? '💰' : '💸')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 500, fontSize: '0.9rem' }} className="truncate">
                        {t.description || t.category_name || 'Sin descripción'}
                      </p>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        {t.category_name} · {new Date(t.date + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                      </p>
                    </div>
                    <span style={{
                      fontWeight: 700, fontSize: '0.95rem', flexShrink: 0,
                      color: t.type === 'income' ? 'var(--green)' : 'var(--red)',
                    }}>
                      {hiddenAmounts['recent'] ? '••••' : `${t.type === 'income' ? '+' : '-'}${formatCurrency(t.amount, currency)}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Link to="/transactions" style={{ display: 'block' }}>
            <button className="btn btn-primary btn-full btn-lg" style={{ marginTop: 8 }}>
              + Registrar movimiento
            </button>
          </Link>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
  periodRow: { display: 'flex', gap: 10 },
  periodSelect: { flex: 1, padding: '10px 14px', fontSize: '0.9rem' },
  balanceCard: {
    background: 'linear-gradient(135deg, #1A1830 0%, #16161F 100%)',
    border: '1px solid rgba(124,111,255,0.2)',
    borderRadius: 'var(--radius-lg)', padding: '28px 24px',
    position: 'relative', overflow: 'hidden',
  },
  balanceGlow: {
    position: 'absolute', width: 300, height: 300, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124,111,255,0.12) 0%, transparent 70%)',
    top: -100, right: -100, pointerEvents: 'none',
  },
  balanceLabel: { fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: 8, fontWeight: 500 },
  prevBalance: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'rgba(255,255,255,0.05)', borderRadius: 10,
    padding: '8px 12px', marginTop: 8,
  },
  balanceRow: { display: 'flex', gap: 0, alignItems: 'center', marginTop: 20 },
  balanceStat: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  balanceStatIcon: { fontSize: 22, color: 'var(--green)', fontWeight: 700 },
  balanceStatLabel: { fontSize: '0.75rem', color: 'var(--text-secondary)' },
  balanceStatVal: { fontSize: '1rem', fontWeight: 700, marginTop: 2 },
  divider: { width: 1, height: 40, background: 'var(--border)', margin: '0 16px' },
  eyeBtn: {
    background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer',
    padding: '4px 8px', borderRadius: 8, fontSize: 14, lineHeight: 1,
    transition: 'background 0.2s',
  },
  eyeBtnSm: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: '2px 4px', borderRadius: 6, fontSize: 12, lineHeight: 1,
  },
  section: { display: 'flex', flexDirection: 'column', gap: 14 },
  sectionTitle: { fontSize: '1.05rem', fontWeight: 700 },
  catRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  subList: {
    marginLeft: 20, marginTop: 4, marginBottom: 8,
    borderLeft: '2px solid var(--border)', paddingLeft: 12,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  subItem: {},
  txnItem: { display: 'flex', alignItems: 'center', gap: 14 },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    padding: '40px 20px', background: 'var(--bg-card)', borderRadius: 'var(--radius)',
    color: 'var(--text-secondary)', textAlign: 'center',
  },
};
